import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { TopProductsResponse } from "types/stats";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { useBreakpoints } from "providers/BreakpointsProvider";
import { TopProductAPI } from "types/stats";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faChartBar, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import DashSegmentedControl from "./v2/DashSegmentedControl";

type PeriodType = "week" | "month" | "semester" | "year";
type SortType = "value" | "quantity" | "margin";

interface ProductMetrics extends TopProductAPI {
  id?: string;
}

export default function TopProdutos() {
  const [viewMode, setViewMode] = useState<SortType>("value");
  const [period, setPeriod] = useState<PeriodType>("month");
  const { breakpoints } = useBreakpoints();

  // Configurações responsivas usando a interface correta
  const isMobile = breakpoints.down("md");
  const isTablet = breakpoints.down("lg") && breakpoints.up("md");
  const cardHeight = isMobile ? "280px" : "380px";
  const itemLimit = isMobile ? 5 : 10;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("pt-BR").format(value);
  };

  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value / 100);
  };

  const formatMargin = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Ajustar período para compatibilidade com backend
  const getBackendPeriod = (frontendPeriod: PeriodType): string => {
    switch (frontendPeriod) {
      case "week":
        return "month"; // Backend não tem week, usar month
      case "month":
        return "month";
      case "semester":
        return "semester";
      case "year":
        return "year";
      default:
        return "month";
    }
  };

  // Função para buscar os dados da API
  const fetchTopProducts = async (): Promise<TopProductsResponse> => {
    const backendPeriod = getBackendPeriod(period);
    const response = await axios.get<TopProductsResponse>(
      `/api/SalesArea/top-products?period=${backendPeriod}&sortBy=${viewMode}&limit=${itemLimit}`
    );
    return response.data;
  };

  // Usando o hook de permissão para tratamento centralizado
  const { getQueryRetryHandler } = usePermissionModal();

  // Hook do TanStack Query para buscar os dados
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["topProducts", period, viewMode, itemLimit],
    queryFn: fetchTopProducts,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["topProducts", period, viewMode, itemLimit],
      defaultMessage: "Você não possui permissão para visualizar os produtos mais vendidos.",
      showModal: false // Não exibir modal, apenas esconder o componente
    }),
  });

  // Verificar se é um erro de permissão (403)
  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;

  // Se não há permissão, simplesmente não renderiza o componente
  if (isAccessDenied) {
    return null;
  }

  // Verificar se os dados estão disponíveis
  const getDisplayProducts = (inputData: TopProductsResponse | null = null): ProductMetrics[] => {
    const dataToUse = inputData || data;
    if (!isLoading && dataToUse?.data?.products) {
      return dataToUse.data.products.map((product, index) => ({
        ...product,
        id: `product-${index}`,
      }));
    }
    return [];
  };

  const displayProducts = getDisplayProducts();

  // Obtém o valor máximo para calcular as proporções da barra de progresso
  const getMaxValue = (products: ProductMetrics[], mode: SortType) => {
    if (products.length === 0) return 1;

    switch (mode) {
      case "value":
        return products[0].value;
      case "quantity":
        return products[0].quantity;
      case "margin":
        return Math.max(...products.map(p => p.margin !== undefined ? p.margin : 0));
      default:
        return products[0].value;
    }
  };

  const maxValue = getMaxValue(displayProducts, viewMode);

  // Renderiza placeholders durante o carregamento
  const renderPlaceholders = () => {
    return Array(itemLimit)
      .fill(0)
      .map((_, index) => (
        <li key={index} className="mb-2 p-2">
          <div className="dash-skeleton mb-2" style={{ height: 14, width: "70%" }} />
          <div className="dash-skeleton mb-2" style={{ height: 10, width: "40%" }} />
          <div className="dash-skeleton" style={{ height: 6, width: "100%" }} />
        </li>
      ));
  };

  // Função para obter o valor de exibição baseado no modo
  const getDisplayValue = (product: ProductMetrics, mode: SortType) => {
    switch (mode) {
      case "value":
        return formatCurrency(product.value);
      case "quantity":
        return formatNumber(product.quantity);
      case "margin":
        return product.margin !== undefined ? formatMargin(product.margin) : "N/A";
      default:
        return formatCurrency(product.value);
    }
  };

  // Função para obter a largura da barra de progresso
  const getProgressWidth = (product: ProductMetrics, mode: SortType) => {
    let value = 0;
    switch (mode) {
      case "value":
        value = product.value;
        break;
      case "quantity":
        value = product.quantity;
        break;
      case "margin":
        value = product.margin !== undefined ? product.margin : 0;
        break;
    }
    return (value / maxValue) * 100;
  };



  // Renderiza o conteúdo da lista considerando o estado de carregamento e erro
  const renderListContent = () => {
    if (isLoading) {
      return <ul className="list-unstyled">{renderPlaceholders()}</ul>;
    }

    if (displayProducts.length === 0) {
      return (
        <div className="dash-empty">
          <div className="dash-empty-icon">
            <FontAwesomeIcon icon={faChartBar} />
          </div>
          <h6>Nenhum produto encontrado</h6>
          <p>Ajuste o período ou aguarde novos dados.</p>
        </div>
      );
    }

    const progressColor = viewMode === "margin" ? "var(--dash-good)" : viewMode === "quantity" ? "var(--dash-accent-2)" : "var(--dash-accent)";

    return (
      <ul className="list-unstyled">
        {displayProducts.map((product, index) => (
          <li
            key={product.id || index}
            className="mb-2 p-2 position-relative"
            style={{ border: "1px solid var(--dash-border-soft)", borderRadius: 10 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="flex-grow-1 d-flex align-items-center">
                <span
                  className="me-2"
                  style={{
                    fontFamily: "var(--dash-font-mono)",
                    fontSize: "0.7rem",
                    color: "var(--dash-accent)",
                    background: "var(--dash-accent-tint)",
                    borderRadius: 999,
                    width: 20,
                    height: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </span>
                <div className="flex-grow-1 min-width-0">
                  <div
                    className="text-truncate"
                    title={product.name}
                    style={{ fontSize: isMobile ? "0.85rem" : "0.9rem", fontWeight: 600, color: "var(--dash-ink)" }}
                  >
                    {product.name}
                  </div>
                  <small className="d-block" style={{ color: "var(--dash-muted)" }}>
                    {viewMode !== "quantity" && `Qtd: ${formatNumber(product.quantity)}`}
                    {viewMode !== "value" && viewMode !== "quantity" && " • "}
                    {viewMode !== "value" && `Valor: ${formatCurrency(product.value)}`}
                    {viewMode !== "margin" && product.margin !== undefined && ` • Margem: ${formatPercentage(product.margin)}`}
                  </small>
                </div>
              </div>
              <div className="text-end ms-2">
                <div
                  style={{
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    fontFamily: "var(--dash-font-mono)",
                    fontWeight: 600,
                    color: "var(--dash-ink)",
                  }}
                >
                  {getDisplayValue(product, viewMode)}
                </div>
                <small style={{ color: "var(--dash-muted)" }}>{product.percentage.toFixed(1)}%</small>
              </div>
            </div>
            <div style={{ height: isMobile ? 5 : 6, borderRadius: 999, background: "var(--dash-border-soft)", overflow: "hidden" }}>
              <div
                role="progressbar"
                style={{
                  height: "100%",
                  borderRadius: 999,
                  background: progressColor,
                  width: `${getProgressWidth(product, viewMode)}%`,
                }}
                aria-valuenow={getProgressWidth(product, viewMode)}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </li>
        ))}
      </ul>
    );
  };

  // Renderiza os controles do cabeçalho com melhor responsividade
  const renderControls = () => (
    <div className="d-flex flex-column gap-2">
      <DashSegmentedControl
        fullWidth
        value={viewMode}
        onChange={setViewMode}
        options={[
          { value: "value", label: isMobile ? "R$" : "Valor" },
          { value: "quantity", label: isMobile ? "Qtd" : "Qtd" },
          { value: "margin", label: isMobile ? "%" : "Margem" },
        ]}
      />
      <DashSegmentedControl
        fullWidth
        value={period}
        onChange={setPeriod}
        options={[
          { value: "week", label: "7d" },
          { value: "month", label: "30d" },
          { value: "semester", label: "6m" },
          { value: "year", label: "1a" },
        ]}
      />
    </div>
  );

  // Se houver erro que não seja de permissão, exibir mensagem genérica
  if (isError && !isAccessDenied) {
    return (
      <div className="dash-card">
        <div className="dash-card-header">
          <div className="dash-card-header-main">
            <div className="dash-card-icon dash-card-icon--warning">
              <FontAwesomeIcon icon={faTrophy} />
            </div>
            <h6 className="dash-card-title">Top {itemLimit} Produtos</h6>
          </div>
          {!isMobile && renderControls()}
        </div>
        {isMobile && <div className="px-3 pb-2">{renderControls()}</div>}
        <div className="dash-card-body d-flex align-items-center justify-content-center" style={{ minHeight: 200 }}>
          <div className="dash-empty">
            <div className="dash-empty-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h6>Erro ao carregar dados</h6>
            <p>Verifique sua conexão.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className="dash-card-header" style={{ flexWrap: "wrap", flexShrink: 0 }}>
        <div className="dash-card-header-main">
          <div className="dash-card-icon dash-card-icon--warning">
            <FontAwesomeIcon icon={faTrophy} />
          </div>
          <div>
            <h6 className="dash-card-title">Top {itemLimit} Produtos</h6>
            <p className="dash-card-subtitle">
              {period === "week" ? "Últimos 7 dias" :
                period === "month" ? "Últimos 30 dias" :
                  period === "semester" ? "Semestre atual" : "Último ano"}
            </p>
          </div>
        </div>
        {!isMobile && (
          <div className="flex-shrink-0" style={{ minWidth: "280px" }}>
            {renderControls()}
          </div>
        )}
        {isMobile && <div className="w-100 mt-2">{renderControls()}</div>}
      </div>
      <div
        style={{
          flex: "1 1 auto",
          minHeight: cardHeight,
          overflowY: "auto",
          padding: isMobile ? "0.5rem" : "0.75rem 1.25rem",
        }}
      >
        {renderListContent()}
      </div>
    </div>
  );
}
